
(function () {
  'use strict';

  if (!window.customElements || customElements.get('klaviyo-newsletter-form')) return;

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var REVISION = '2025-01-15';
  var TIMEOUT_MS = 12000;
  var MAX_ATTEMPTS = 2;
  var RETRY_DELAY_MS = 700;
  var MAX_EMAIL_LENGTH = 254;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  var MESSAGES = {
    required: 'Please enter your email address.',
    invalid: 'Please enter a valid email address.',
    offline: 'You appear to be offline. Please check your connection and try again.',
    generic: 'Something went wrong. Please try again in a moment.'
  };

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function isRetryable(status) {
    return status === 0 || status === 429 || status >= 500;
  }

  class KlaviyoNewsletterForm extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.formEl = this.querySelector('[data-newsletter-form]');
      this.inputEl = this.querySelector('[data-newsletter-email]');
      this.buttonEl = this.querySelector('[data-newsletter-submit]');
      this.statusEl = this.querySelector('[data-newsletter-status]');
      this.successEl = this.querySelector('[data-newsletter-success]');
      this.honeypotEl = this.querySelector('[data-newsletter-hp]');

      if (!this.formEl || !this.inputEl) return;

      this.submitting = false;
      this.subscribed = false;
      this.buttonLabel = this.buttonEl ? this.buttonEl.innerHTML : '';

      this._onSubmit = this.handleSubmit.bind(this);
      this._onInput = this.clearError.bind(this);

      this.formEl.addEventListener('submit', this._onSubmit);
      this.inputEl.addEventListener('input', this._onInput);
    }

    disconnectedCallback() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
    }

    get companyId() {
      return (this.dataset.companyId || '').trim();
    }

    get listId() {
      return (this.dataset.listId || '').trim();
    }

    get source() {
      return (this.dataset.source || '').trim() || 'Website Newsletter Form';
    }

    get location() {
      return (this.dataset.location || '').trim() || 'unknown';
    }

    handleSubmit(event) {
      event.preventDefault();

      if (this.submitting || this.subscribed) return;

      if (this.honeypotEl && this.honeypotEl.value) {
        this.showSuccess();
        return;
      }

      var email = (this.inputEl.value || '').trim();

      if (!email) return this.showError(MESSAGES.required);
      if (email.length > MAX_EMAIL_LENGTH || !EMAIL_RE.test(email)) {
        return this.showError(MESSAGES.invalid);
      }
      if (navigator.onLine === false) return this.showError(MESSAGES.offline);

      if (!this.companyId || !this.listId) {
        console.warn('[klaviyo-newsletter-form] Missing data-company-id or data-list-id.');
        return this.showError(MESSAGES.generic);
      }

      this.subscribe(email);
    }

    subscribe(email) {
      var self = this;
      this.setLoading(true);
      this.clearError();

      this.send(email, 1)
        .then(function () {
          self.subscribed = true;
          self.setLoading(false);
          self.showSuccess();
          self.dispatchEvent(
            new CustomEvent('newsletter:success', {
              bubbles: true,
              detail: { email: email, location: self.location }
            })
          );
        })
        .catch(function (error) {
          self.setLoading(false);
          if (error && error.name === 'AbortError' && !self.isConnected) return;
          self.showError(navigator.onLine === false ? MESSAGES.offline : MESSAGES.generic);
          self.dispatchEvent(
            new CustomEvent('newsletter:error', {
              bubbles: true,
              detail: { email: email, location: self.location, error: error }
            })
          );
        });
    }

    send(email, attempt) {
      var self = this;
      var controller = new AbortController();
      this.controller = controller;

      var timer = setTimeout(function () {
        controller.abort();
      }, TIMEOUT_MS);

      return fetch(ENDPOINT + '?company_id=' + encodeURIComponent(this.companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        body: JSON.stringify(this.payload(email)),
        signal: controller.signal
      })
        .then(function (response) {
          if (response.ok) return response;
          var error = new Error('Klaviyo responded with ' + response.status);
          error.status = response.status;
          throw error;
        })
        .catch(function (error) {
          var status = typeof error.status === 'number' ? error.status : 0;
          var aborted = error && error.name === 'AbortError';
          var canRetry = attempt < MAX_ATTEMPTS && !(aborted && !self.isConnected) && isRetryable(status);

          if (!canRetry) throw error;
          return wait(RETRY_DELAY_MS * attempt).then(function () {
            return self.send(email, attempt + 1);
          });
        })
        .finally(function () {
          clearTimeout(timer);
          if (self.controller === controller) self.controller = null;
        });
    }

    payload(email) {
      return {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.source,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: {
                    signup_source: this.source,
                    newsletter_location: this.location
                  },
                  subscriptions: {
                    email: { marketing: { consent: 'SUBSCRIBED' } }
                  }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: this.listId } } }
        }
      };
    }

    setLoading(loading) {
      this.submitting = loading;
      this.classList.toggle('is-loading', loading);
      this.inputEl.readOnly = loading;

      if (!this.buttonEl) return;
      this.buttonEl.disabled = loading;
      this.buttonEl.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (loading) {
        this.buttonEl.innerHTML = this.buttonEl.dataset.loadingLabel || this.buttonLabel;
      } else {
        this.buttonEl.innerHTML = this.buttonLabel;
      }
    }

    showError(message) {
      this.inputEl.setAttribute('aria-invalid', 'true');
      this.inputEl.classList.add('newsletter__input--error');

      if (this.statusEl) {
        this.statusEl.textContent = message;
        this.statusEl.classList.add('newsletter__message--error');
        this.statusEl.hidden = false;
      }

      try {
        this.inputEl.focus({ preventScroll: true });
      } catch (e) {
        this.inputEl.focus();
      }
    }

    clearError() {
      this.inputEl.removeAttribute('aria-invalid');
      this.inputEl.classList.remove('newsletter__input--error');
      if (this.statusEl) {
        this.statusEl.textContent = '';
        this.statusEl.hidden = true;
        this.statusEl.classList.remove('newsletter__message--error');
      }
    }

    showSuccess() {
      this.subscribed = true;
      this.clearError();
      this.inputEl.value = '';
      this.classList.add('is-success');

      if (this.formEl) this.formEl.hidden = true;
      if (!this.successEl) return;

      this.successEl.hidden = false;
      this.successEl.setAttribute('tabindex', '-1');
      try {
        this.successEl.focus({ preventScroll: true });
      } catch (e) {}
    }
  }

  customElements.define('klaviyo-newsletter-form', KlaviyoNewsletterForm);
})();
